import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { LoadingOutlined } from '@ant-design/icons'
import { Dropdown, List, notification, Typography } from 'antd'
import clsx from 'clsx'
import dayjs from 'dayjs'
import useGetAnnotationColor from 'hooks/useGetAnnotationColor'
import { useRecoilState } from 'recoil'
import Api from 'services/api'
import { currentAnnotation } from 'store/krpanoStore'
import useSWR from 'swr'
import {
  AnnotationType,
  BuildingFile,
  FolderItem,
  ListBuildingFiles
} from 'types/building'

import Button from 'components/atoms/Button'
import EllipsisIcon from 'components/icons/EllipsisIcon'
import RightArrowIcon from 'components/icons/RightArrowIcon'

import useFileDuplicateCheck from '../hooks/useFileDuplicateCheck'

import DuplicateFileModal from './DuplicateFileModal'
import ModalEditFileName from './ModalEditFileName'
import ModalFileInfo from './ModalFileInfo'
import PreviewFile from './PreviewFile'

type Props = {
  selectedChildrenFolder: FolderItem | null
  setSelectedChildrenFolder: Dispatch<SetStateAction<FolderItem | null>>
  childrenFolder: FolderItem[] | null
  setIsAddingFile: Dispatch<SetStateAction<boolean>>
  setExistsFile: Dispatch<SetStateAction<BuildingFile[]>>
  setPreviewFileIndex: Dispatch<SetStateAction<number | undefined>>
}

export default function FolderList({
  childrenFolder,
  selectedChildrenFolder,
  setSelectedChildrenFolder,
  setIsAddingFile,
  setExistsFile,
  setPreviewFileIndex
}: Props) {
  const { id } = useParams()
  const { t } = useTranslation()
  const [currentSelected, setCurrentSelected] =
    useRecoilState(currentAnnotation)
  const { getAnnotationBgColor } = useGetAnnotationColor()

  const annotationBgColor = useMemo(
    () =>
      currentSelected?.type
        ? getAnnotationBgColor(currentSelected.type)
        : getAnnotationBgColor(AnnotationType.ARCHITECTURE),
    [currentSelected?.type, getAnnotationBgColor]
  )

  const containerRef = useRef<HTMLDivElement>(null)
  const [nameWidth, setNameWidth] = useState(450)
  const [sizeWidth, setSizeWidth] = useState(100)
  const resizingTarget = useRef<'name' | 'size' | null>(null)

  // Buffer for other components (excluding Name and Size):
  // Divider1(4) + Divider2(4) + Menu(80) + Spacer(12) + 6 Gaps(12*6=72) + Internal Padding(40) + Modal Parent Padding(80) + Min Date(40)
  // Total = 4+4+80+12+72+40+80+40 = 332
  const FIXED_BUFFER = 332

  // Function to calculate maximum allowed width for the name column
  const getMaxNameWidth = useCallback(
    (containerWidth: number) => {
      return Math.max(50, containerWidth - FIXED_BUFFER - sizeWidth)
    },
    [sizeWidth]
  )

  // Update nameWidth when container size changes (responsive)
  useEffect(() => {
    const parent = containerRef.current?.parentElement
    if (!parent) return

    const handleResize = () => {
      const parentWidth = parent.clientWidth
      if (parentWidth > 0) {
        const maxAllowed = getMaxNameWidth(parentWidth)
        setNameWidth((prev) => Math.min(prev, maxAllowed))
      }
    }

    const resizeObserver = new ResizeObserver(handleResize)
    resizeObserver.observe(parent)

    // Initial check
    handleResize()

    return () => resizeObserver.disconnect()
  }, [getMaxNameWidth])

  const [sortConfig, setSortConfig] = useState<{
    key: 'name' | 'createdAt'
    order: 'asc' | 'desc'
  }>({ key: 'createdAt', order: 'desc' })

  const [isUploading, setIsUploading] = useState(false)
  const [selectedFileInfo, setSelectedFileInfo] = useState<BuildingFile | null>(
    null
  )
  const [renamingFile, setRenamingFile] = useState<BuildingFile | null>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const [hasScroll, setHasScroll] = useState(false)
  const [scrollbarWidth, setScrollbarWidth] = useState(0)

  const onMouseMove = (e: MouseEvent) => {
    const target = resizingTarget.current
    if (!target) return

    const parentWidth = containerRef.current?.parentElement?.clientWidth || 510
    const totalBudget = parentWidth - FIXED_BUFFER

    if (target === 'name') {
      setNameWidth((prev) =>
        Math.max(50, Math.min(prev + e.movementX, totalBudget - sizeWidth))
      )
    } else if (target === 'size') {
      setSizeWidth((prev) =>
        Math.max(60, Math.min(prev - e.movementX, totalBudget - nameWidth))
      )
    }
  }

  const onMouseUp = () => {
    resizingTarget.current = null
    document.removeEventListener('mousemove', onMouseMove)
    document.removeEventListener('mouseup', onMouseUp)
  }

  const onMouseDown = (target: 'name' | 'size') => (e: React.MouseEvent) => {
    resizingTarget.current = target
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
    e.preventDefault()
  }

  const handleSort = useCallback((key: 'name' | 'createdAt') => {
    setSortConfig((prev) => {
      const isSameKey = prev.key === key
      let nextOrder: 'asc' | 'desc'
      if (isSameKey) {
        nextOrder = prev.order === 'asc' ? 'desc' : 'asc'
      } else {
        nextOrder = 'desc'
      }
      return { key, order: nextOrder }
    })
  }, [])

  const annotationsFile: BuildingFile[] = useMemo(() => {
    const filterList = currentSelected?.files?.filter(
      (file) => file.category === selectedChildrenFolder?.id
    )
    if (!filterList) return []

    return [...filterList].sort((a, b) => {
      const { key, order } = sortConfig
      let comparison = 0
      if (key === 'name') {
        comparison = (a.name || '').localeCompare(b.name || '')
      } else if (key === 'createdAt') {
        const dateA = dayjs(a.updatedAt || a.createdAt)
        const dateB = dayjs(b.updatedAt || b.createdAt)
        if (dateA.isSame(dateB)) {
          comparison = 0
        } else {
          comparison = dateA.isAfter(dateB) ? 1 : -1
        }
      }
      return order === 'asc' ? comparison : -comparison
    })
  }, [currentSelected?.files, selectedChildrenFolder, sortConfig])

  const { data: allBuildingFiles } = useSWR<ListBuildingFiles>({
    url: 'v1/buildings/files',
    params: {
      buildingId: id,
      category: selectedChildrenFolder?.id
    }
  })

  useEffect(() => {
    setExistsFile(annotationsFile)
  }, [annotationsFile, setExistsFile])

  useEffect(() => {
    const checkScroll = () => {
      if (listRef.current) {
        const isScrollable =
          listRef.current.scrollHeight > listRef.current.clientHeight
        setHasScroll(isScrollable)
        setScrollbarWidth(
          listRef.current.offsetWidth - listRef.current.clientWidth
        )
      }
    }

    checkScroll()
    const timeout = setTimeout(checkScroll, 100)

    window.addEventListener('resize', checkScroll)
    return () => {
      clearTimeout(timeout)
      window.removeEventListener('resize', checkScroll)
    }
  }, [annotationsFile, selectedChildrenFolder])

  const formatBytes = (
    bytes: number | string | undefined | null,
    decimals = 1
  ) => {
    if (!bytes) return '--'
    const k = 1024
    const dm = decimals < 0 ? 0 : decimals
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
    const i = Math.floor(Math.log(Number(bytes)) / Math.log(k))
    return `${parseFloat((Number(bytes) / k ** i).toFixed(dm))}${sizes[i]}`
  }

  const formatFileInfo = (item: BuildingFile) => {
    const { extension, name } = item
    const ext = extension?.toLowerCase() || name.split('.').pop()?.toLowerCase()
    const { extraInfo } = item

    if (ext === 'pdf') {
      const pageCount = extraInfo?.page_count
      return pageCount ? `${pageCount}ページ` : '--'
    }

    const typesVideo = ['mov', 'mp4']
    if (ext && typesVideo.includes(ext)) {
      const duration = extraInfo?.duration
      if (duration) {
        const mins = Math.floor(duration / 60)
        const secs = Math.floor(duration % 60)
        return `${mins}:${secs.toString().padStart(2, '0')}`
      }
      // If duration is missing (local file), show file size as fallback
      return '--'
    }

    return formatBytes(extraInfo?.size || item.size)
  }

  const dropdownItems = (item: BuildingFile) => [
    { key: 'open', label: '開く', disabled: item.id < 0 },
    { key: 'rename', label: '名前を変更' },
    { key: 'download', label: 'ダウンロード', disabled: item.id < 0 },
    { key: 'info', label: 'ファイル情報', disabled: item.id < 0 },
    { key: 'delete', label: '削除' }
  ]
  // ─── processFiles must be defined BEFORE useFileDuplicateCheck ───────────────
  // We use a stable ref so the hook always calls the latest version without
  // triggering a re-render cycle, and without a forward-reference lint error.
  const processFilesRef = useRef<(files: File[], option?: number) => void>(
    () => {}
  )

  const processFiles = (files: File[], option?: number) => {
    setIsUploading(true)
    const category = selectedChildrenFolder?.id || 0
    const newFiles: BuildingFile[] = []

    files.forEach((file, index) => {
      let finalName = file.name
      let overwriteID: number | undefined
      let mode: 'overwrite' | 'keep' | undefined

      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      const match = duplicateFiles.find((d) => d.newFile.name === file.name)
      const effectiveOverwriteID =
        match &&
        (match.oldFile.id > 0 ? match.oldFile.id : match.oldFile.overwriteID)

      if (option === 1) {
        // Replacement mode
        if (effectiveOverwriteID) {
          overwriteID = effectiveOverwriteID
          mode = 'overwrite'
        }
      } else if (option === 2) {
        // Keep both (Numbering)
        const allExistingNames = [
          ...(allBuildingFiles?.files?.files?.map((f) => f.name) || []),
          ...(currentSelected?.files || []).map((f) => f.name),
          ...newFiles.map((f) => f.name)
        ]

        const lastDotIndex = file.name.lastIndexOf('.')
        const extension =
          lastDotIndex !== -1 ? file.name.substring(lastDotIndex) : ''
        const baseName =
          lastDotIndex !== -1 ? file.name.substring(0, lastDotIndex) : file.name

        let counter = 1
        while (allExistingNames.includes(finalName)) {
          finalName = `${baseName}(${counter})${extension}`
          counter += 1
        }
      }

      const localUrl = URL.createObjectURL(file)
      const newFileObj: BuildingFile = {
        id: -Date.now() - index - Math.floor(Math.random() * 1000000),
        name: finalName,
        s3Path: localUrl,
        mimeType: file.type,
        extension: finalName.split('.').pop()?.toLowerCase() || '',
        createdAt: new Date().toISOString(),
        size: file.size,
        thumbnail: localUrl,
        category,
        pointId: null,
        takenAt: dayjs(file.lastModified).format('YYYY-MM-DDTHH:mm:ssZ'),
        rawFile: file as any,
        overwriteID,
        mode
      }
      newFiles.push(newFileObj)
    })

    setCurrentSelected((prev) => {
      if (!prev) return prev
      let filteredOldFiles = prev.files || []
      if (option === 1) {
        // Remove locally added files with the same name if overwriting
        const overwrittenNames = files.map((f) => f.name)
        filteredOldFiles = filteredOldFiles.filter(
          (f) => !overwrittenNames.includes(f.name)
        )
      }
      const updatedFiles = [...filteredOldFiles, ...newFiles]
      return {
        ...prev,
        files: updatedFiles
      }
    })

    setIsUploading(false)
  }

  // Keep the ref in sync with the latest processFiles closure on every render
  useEffect(() => {
    processFilesRef.current = processFiles
  })

  const {
    duplicateFiles,
    showDuplicateModal,
    selectedDuplicateOption,
    setSelectedDuplicateOption,
    fileInputRef: sharedFileInputRef,
    handleFileUpload,
    handleConfirmDuplicate,
    handleCancelDuplicate,
    setDuplicateFiles
  } = useFileDuplicateCheck({
    currentListFile: currentSelected?.files || [],
    allBuildingFiles,
    processFiles: (files: File[], option?: number) =>
      processFilesRef.current(files, option),
    allowedExtensions: ['jpg', 'jpeg', 'png', 'gif', 'pdf', 'mov', 'mp4']
  })
  // ─────────────────────────────────────────────────────────────────────────────

  const handleRemoveFileFromAnnotation = (item: BuildingFile) => {
    setCurrentSelected((prev) => {
      if (!prev) return prev
      return {
        ...prev,
        files: prev.files?.filter((f) => f.id !== item.id)
      }
    })
  }

  const handleDownloadFile = async (item: BuildingFile) => {
    try {
      notification.info({
        message: t('building.downloading'),
        key: 'downloading',
        icon: <LoadingOutlined className="theme-text-primary" />
      })
      // Fetch fresh file detail from API to avoid expired S3 URLs
      const fileDetail = await Api.get<BuildingFile>(
        `/v1/buildings/files/${item.id}`
      )
      const downloadPath = fileDetail?.s3Path || item.s3Path

      const response = await fetch(downloadPath)
      if (!response.ok) throw new Error('Failed to fetch file content')
      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = item.name
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
      notification.success({
        message: t('building.downloadSuccess'),
        key: 'downloading'
      })
    } catch (error) {
      // console.error('Download error:', error)
      notification.error({
        message: t('common.error'),
        key: 'downloading'
      })
    }
  }

  const handleMenuClick = (key: string, item: BuildingFile) => {
    switch (key) {
      case 'open': {
        const index = annotationsFile.findIndex((f) => f.id === item.id)
        setPreviewFileIndex(index)
        break
      }
      case 'rename':
        setRenamingFile(item)
        break
      case 'download':
        handleDownloadFile(item)
        break
      case 'info':
        setSelectedFileInfo(item)
        break
      case 'delete':
        handleRemoveFileFromAnnotation(item)
        break
      default:
        break
    }
  }

  if (selectedChildrenFolder) {
    return (
      <div
        className="flex flex-col grow min-w-0 overflow-hidden"
        ref={containerRef}
      >
        <div
          className="grid items-center px-5 py-2 border-0 border-b border-solid border-[#EEEFF2] group"
          style={{
            gridTemplateColumns: `${nameWidth}px 4px 1fr 4px ${sizeWidth}px 80px`,
            gap: '12px',
            paddingRight: hasScroll ? `${20 + scrollbarWidth}px` : '20px'
          }}
        >
          <div
            className="flex items-center gap-1 cursor-pointer select-none"
            onClick={() => handleSort('name')}
          >
            <Typography
              className={clsx('font-bold text-[14px] whitespace-nowrap', {
                'text-[#021120]': sortConfig.key === 'name',
                'text-[#A0A0A0]': sortConfig.key !== 'name'
              })}
            >
              名前
              {sortConfig.key === 'name' &&
                (sortConfig.order === 'asc' ? '↑' : '↓')}
            </Typography>
          </div>
          <div
            onMouseDown={onMouseDown('name')}
            className="h-full w-[4px] cursor-col-resize bg-transparent border-0 border-l border-r border-solid border-transparent group-hover:border-[#A0A0A0] transition-colors"
          />
          <div
            className="flex items-center gap-1 cursor-pointer select-none"
            onClick={() => handleSort('createdAt')}
          >
            <Typography
              className={clsx('font-bold text-[14px] whitespace-nowrap', {
                'text-[#021120]': sortConfig.key === 'createdAt',
                'text-[#A0A0A0]': sortConfig.key !== 'createdAt'
              })}
            >
              更新日
              {sortConfig.key === 'createdAt' &&
                (sortConfig.order === 'asc' ? '↑' : '↓')}
            </Typography>
          </div>
          <div
            onMouseDown={onMouseDown('size')}
            className="h-full w-[4px] cursor-col-resize bg-transparent border-0 border-l border-r border-solid border-transparent group-hover:border-[#A0A0A0] transition-colors"
          />
          <Typography className="text-[#021120] font-bold text-[14px] text-left">
            容量
          </Typography>
          <div />
        </div>

        <div
          ref={listRef}
          style={{
            height: 300,
            overflowY: 'auto'
          }}
          className="overflow-x-hidden"
        >
          <List
            dataSource={annotationsFile}
            renderItem={(item) => (
              <List.Item
                className="!grid items-center !px-5 min-h-[60px] cursor-pointer border-0 border-b border-solid border-[#EEEFF2] hover:bg-[#F3F6F6]"
                style={{
                  gridTemplateColumns: `${nameWidth}px 4px 1fr 4px ${sizeWidth}px 80px`,
                  gap: '12px'
                }}
              >
                <div className="flex items-center gap-3 overflow-hidden min-w-0">
                  <div className="w-[60px] h-[40px] shrink-0 flex items-center justify-center">
                    <PreviewFile file={item} size="small" />
                  </div>
                  <Typography className="text-[#021120] text-[13px] leading-[20px] font-medium truncate ">
                    {item.name}
                  </Typography>
                </div>
                <div />
                <Typography className="text-[#021120] text-[13px] font-medium whitespace-nowrap">
                  {item.updatedAt || item.id < 0
                    ? dayjs(item.updatedAt || item.createdAt).format(
                        'YYYY/MM/DD'
                      )
                    : ''}
                </Typography>
                <div />
                <Typography className="text-[#021120] text-[13px] font-medium whitespace-nowrap text-left">
                  {formatFileInfo(item)}
                </Typography>
                <Dropdown
                  menu={{
                    items: dropdownItems(item),
                    onClick: ({ key }) => handleMenuClick(key, item),
                    style: { backgroundColor: 'white' }
                  }}
                  trigger={['click']}
                  overlayClassName="annotation-folder-dropdown"
                >
                  <div
                    className="flex justify-center items-center w-6 h-6 rounded hover:bg-[#3263B71A] transition-colors ml-auto"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <EllipsisIcon className="cursor-pointer" />
                  </div>
                </Dropdown>
              </List.Item>
            )}
          />
        </div>

        <div className="flex gap-10 justify-center mt-10 pb-5">
          <input
            type="file"
            ref={sharedFileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileUpload}
            multiple
          />
          <Button
            sizing="w-[240px] h-[50px]"
            className={clsx(
              annotationBgColor,
              'text-white border-0 hover:!text-white hover:opacity-80'
            )}
            onClick={() => setIsAddingFile(true)}
          >
            {t('building.selectFile')}
          </Button>
          <Button
            sizing="w-[240px] h-[50px]"
            className={clsx(
              annotationBgColor,
              'text-white border-0 hover:!text-white hover:opacity-80'
            )}
            onClick={() => sharedFileInputRef.current?.click()}
            loading={isUploading}
          >
            {t('common.upload')}
          </Button>
        </div>
        {selectedFileInfo && (
          <ModalFileInfo
            file={selectedFileInfo}
            onCancel={() => setSelectedFileInfo(null)}
          />
        )}
        {renamingFile && (
          <ModalEditFileName
            file={renamingFile}
            existingNames={[
              ...(allBuildingFiles?.files.files
                .filter((f) => f.id !== renamingFile.id)
                .map((f) => f.name) || []),
              ...(currentSelected?.files || [])
                .filter((f) => f.id !== renamingFile.id)
                .map((f) => f.name)
            ]}
            onCancel={() => setRenamingFile(null)}
            onSuccess={(newName) => {
              setCurrentSelected((prev) => {
                if (!prev) return prev
                return {
                  ...prev,
                  files: prev.files?.map((f) => {
                    if (f.id === renamingFile.id) {
                      // Store original name if not already stored to track changes for PATCH API
                      const originalName = f.originalName || f.name
                      return { ...f, name: newName, originalName }
                    }
                    return f
                  })
                }
              })
              setRenamingFile(null)
            }}
          />
        )}
        <DuplicateFileModal
          open={showDuplicateModal}
          duplicateFiles={duplicateFiles}
          selectedOption={selectedDuplicateOption}
          setSelectedOption={setSelectedDuplicateOption}
          onCancel={handleCancelDuplicate}
          onConfirm={handleConfirmDuplicate}
          afterClose={() => setDuplicateFiles([])}
        />
      </div>
    )
  }

  return (
    <List
      style={{
        height: 398,
        overflow: 'auto'
      }}
      dataSource={childrenFolder || []}
      renderItem={(item: FolderItem) => (
        <List.Item
          className="flex !pl-[10px] h-[60px] !pr-5 cursor-pointer"
          onClick={() =>
            setSelectedChildrenFolder({
              id: item.id,
              label: item.label
            })
          }
        >
          <Typography className="text-[#021120] leading-[23.17px] font-medium">
            {item.label}
          </Typography>
          <RightArrowIcon color="#3B3C46" size={24} />
        </List.Item>
      )}
    />
  )
}
